import { useEffect, useRef } from 'react';

export type ShortcutHandler = (e: KeyboardEvent) => void;
export type ShortcutMap = Record<string, ShortcutHandler>;

/**
 * Registers global keydown handlers keyed by lowercase key (e.g. "a", "escape", "/").
 * Skips firing when the focus is in an input/textarea/contentEditable so cashiers can
 * still type into the search field and login form.
 *
 * Modifier-key combos: prefix the key with "mod+" for ⌘/Ctrl (e.g. "mod+k").
 */
export function useKeyboardShortcuts(map: ShortcutMap, enabled = true) {
  const ref = useRef(map);
  useEffect(() => {
    ref.current = map;
  });

  useEffect(() => {
    if (!enabled) return;
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName?.toLowerCase();
      const editable =
        tag === 'input' ||
        tag === 'textarea' ||
        target?.isContentEditable === true ||
        target?.getAttribute('role') === 'textbox';

      const key = e.key.toLowerCase();

      // Allow Escape to bubble even from inside inputs (so the drawer can close).
      if (editable && key !== 'escape') return;

      const mod = e.metaKey || e.ctrlKey;
      const composedKey = mod ? `mod+${key}` : key;

      const fn = ref.current[composedKey] ?? ref.current[key];
      if (!fn) return;
      e.preventDefault();
      fn(e);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [enabled]);
}
